// Topic taxonomy — the canonical vocabulary the whole site clusters around.
//
// Nothing here invents content. A topic is a controlled label plus the
// alias/keyword vocabulary used to detect that label in first-party,
// repository-backed fields (author-written tags, titles, summaries,
// categories). An item only joins a topic when its own text or tags contain
// that vocabulary.

export interface HubLink {
  to: string;
  label: string;
  note?: string;
}

export interface Topic {
  slug: string;
  name: string;
  /** Canonical one-sentence definition. Reused by the glossary and FAQ schema. */
  definition: string;
  /** Longer canonical description for the hub page. */
  description: string;
  /** Tag spellings that map onto this topic (normalized, lowercase). */
  aliases: string[];
  /** Word-boundary phrases detected in titles, summaries, and body text. */
  keywords: string[];
  /**
   * Authority territories are the six permanent editorial hubs of the site.
   * They carry an editorial introduction and curated first-party destinations
   * on top of the dynamic cluster every topic already receives.
   */
  territory?: boolean;
  /** Display order within the territory row. */
  territoryOrder?: number;
  /** Editorial introduction, in the Buddha KA voice. Paragraphs. */
  intro?: string[];
  /** Curated ecosystem destinations shown on the hub page. */
  hubLinks?: HubLink[];
}


export const TOPICS: readonly Topic[] = [
  {
    slug: "meditation",
    name: "Meditation",
    definition:
      "Meditation, in the KAfrequency canon, is trained attention held long enough for the nervous system to reorganize around stillness.",
    description:
      "Sitting practice as it is taught across the catalog: the activations that structure it, the lessons that explain it, and the music written to be listened to the way one would sit.",
    aliases: ["meditation", "stillness", "sitting", "zazen", "presence"],
    keywords: ["meditation", "meditate", "stillness", "sit in silence", "presence practice"],
  },
  {
    slug: "consciousness",
    name: "Consciousness",
    definition:
      "Consciousness is the awareness that observes experience without being consumed by it — the field the 47 Gates map.",
    description:
      "The study of awareness itself: the 47 Gates, the Codex vocabulary, and every teaching that treats the witness as something trainable rather than given.",
    aliases: ["consciousness", "awareness", "witness", "47 gates", "gates", "self-realization"],
    keywords: ["consciousness", "awareness", "the witness", "47 gates", "higher self"],
  },
  {
    slug: "frequency",
    name: "Frequency",
    definition:
      "Frequency is the measurable rate of a vibration and, in this canon, the organizing principle behind how a piece of music is tuned and used.",
    description:
      "Tuning, resonance, and sound as instruction — how each release carries a stated frequency and what that frequency is intended to do in the body.",
    aliases: ["frequency", "hz", "432hz", "528hz", "resonance", "vibration", "sound healing", "tuning"],
    keywords: ["frequency", "hz", "resonance", "vibration", "tuning", "solfeggio"],
  },
  {
    slug: "breathwork",
    name: "Breathwork",
    definition:
      "Breathwork is the deliberate use of breath pattern and pace to change nervous-system state.",
    description:
      "The first instrument. Every activation with a breath pattern, every lesson on pranayama, and the recordings paced for breathing.",
    aliases: ["breath", "breathwork", "pranayama", "breathing", "prana"],
    keywords: ["breathwork", "breath pattern", "pranayama", "inhale", "exhale", "breathing"],
  },
  {
    slug: "tai-chi",
    name: "Tai Chi",
    definition:
      "Tai chi is a slow internal movement art that trains balance, structure, and continuous attention through form.",
    description:
      "Internal movement practice — form, rooting, and the transfer of stillness into motion.",
    aliases: ["tai chi", "taichi", "taiji", "qigong", "chi kung", "internal arts"],
    keywords: ["tai chi", "taiji", "qigong", "internal art", "standing form"],
  },
  {
    slug: "kemetic-studies",
    name: "Kemetic Studies",
    definition:
      "Kemetic studies is the study of ancient Nile Valley spiritual systems, their symbols, and their transmission.",
    description:
      "The Kemetic lineage as it appears across the canon: symbol, ka, ma'at, and the architecture behind the temple language of this work.",
    aliases: ["kemetic", "kemet", "ka", "maat", "ma'at", "ankh", "nile valley", "african spirituality"],
    keywords: ["kemetic", "kemet", "ma'at", "ankh", "nile valley", "the ka"],
  },
  {
    slug: "buddhism",
    name: "Buddhism",
    definition:
      "Buddhism is the body of teaching built on the Four Noble Truths and the Eightfold Path.",
    description:
      "Sutra, path, and practice — where the Buddhist frame enters the teachings and how it is used here.",
    aliases: ["buddhism", "buddhist", "dharma", "sutra", "eightfold path", "noble truths", "bodhi"],
    keywords: ["buddhism", "buddhist", "dharma", "sutra", "eightfold", "noble truth", "sangha"],
  },
  {
    slug: "martial-arts",
    name: "Martial Arts",
    definition:
      "Martial arts here means disciplined combative training treated as a vehicle for attention, structure, and restraint.",
    description:
      "Discipline as spiritual technology — training, form, restraint, and the ethics of capability.",
    aliases: ["martial arts", "martial", "kung fu", "discipline", "warrior", "combat"],
    keywords: ["martial art", "kung fu", "warrior", "training discipline", "the warrior"],
  },
  {
    slug: "spiritual-science",
    name: "Spiritual Science",
    definition:
      "Spiritual science is the practice of testing inner disciplines against observable, repeatable effects rather than belief alone.",
    description:
      "Where measurement meets practice: nervous-system effects, repeatability, and the labeled provenance standard used across the Academy.",
    aliases: ["spiritual science", "science", "nervous system", "neuroscience", "evidence", "provenance", "buddhism", "kemetic", "yoga", "esoteric"],
    keywords: ["nervous system", "spiritual science", "evidence", "research", "physiolog"],
    territory: true,
    territoryOrder: 4,
    intro: [
      "Spiritual science is the discipline of treating inner work the way an engineer treats a structure: state what is claimed, state where the claim comes from, and see whether it holds when it is used.",
      "This territory keeps three kinds of knowledge visibly separate. Historical and traditional material — Buddhist, Kemetic, yogic, esoteric — is presented as what those traditions actually taught. Evidence is presented as evidence, with its limits. Buddha KA's own synthesis is labeled as interpretation, never smuggled in as ancient authority.",
    ],
    hubLinks: [
      { to: "/artist/buddha-ka", label: "Buddha KA — the artist page", note: "Canonical artist entity" },
      { to: "/academy", label: "The Academy", note: "Lessons with labeled provenance" },
      { to: "/books", label: "Books by Buddha KA", note: "Written systems and study material" },
      { to: "/activations", label: "Daily activations", note: "Practice you can actually run" },
    ],
  },

  {
    slug: "music-production",
    name: "Music Production",
    definition:
      "Music production is the craft of composing, recording, arranging, and mixing a finished piece of sound.",
    description:
      "How the catalog is made: composition, arrangement, credits, and production notes published with the work.",
    aliases: ["production", "music production", "producer", "mixing", "mastering", "beats", "instrumental"],
    keywords: ["production notes", "mixing", "mastering", "produced by", "arrangement", "studio"],
  },
  {
    slug: "beat-licensing",
    name: "Beat Licensing",
    definition:
      "Beat licensing is the grant of defined usage rights to an instrumental composition under stated terms.",
    description:
      "The licensing system: tiers, rights, delivery, and the catalog of instrumentals available under license.",
    aliases: ["beat licensing", "license", "licensing", "beats", "bkls", "royalty", "rights"],
    keywords: ["license tier", "beat licensing", "exclusive rights", "royalty", "usage rights"],
  },
  {
    slug: "buddha-ka",
    name: "Buddha KA",
    definition:
      "Buddha KA is the artist, author, and architect behind KAfrequency — a body of work uniting music, consciousness practice, and inner sovereignty.",
    description:
      "The artist territory: identity, worldview, the shape of the catalog, and the philosophy that decides what gets made and what gets cut.",
    aliases: ["buddha ka", "artist identity", "buddhaka", "artist notes", "who is buddha ka"],
    keywords: ["buddha ka's", "artist identity"],
    territory: true,
    territoryOrder: 1,
    intro: [
      "Buddha KA is an independent artist and author whose work treats music, writing, and daily practice as one continuous system rather than three separate careers.",
      "This territory is the entry point. It holds the canonical introduction to the artist, the worldview underneath the songs, and the map of how the music, the books, the activations, and the Academy fit together.",
    ],
    hubLinks: [
      { to: "/artist/buddha-ka", label: "Buddha KA — official artist page", note: "Streaming profiles and verified identity" },
      { to: "/music", label: "The music catalog", note: "Albums, EPs, and singles" },
      { to: "/about", label: "Biography", note: "Origin and body of work" },
      { to: "/books", label: "Books", note: "The written systems" },
    ],
  },
  {
    slug: "kafrequency",
    name: "KAfrequency",
    definition:
      "KAfrequency is Buddha KA's framework for turning consciousness into practice — attention treated as currency, peace treated as structure, and spirituality held to the standard of implementation.",
    description:
      "The original framework: attention discipline, inner sovereignty, and frequency understood as the quality and consistency of how a life is actually run.",
    aliases: ["kafrequency", "ka frequency", "inner sovereignty", "sovereignty", "attention discipline", "practice"],
    keywords: ["kafrequency", "inner sovereignty"],
    territory: true,
    territoryOrder: 2,
    intro: [
      "KAfrequency is not a mood and not a number in hertz. It is a framework: consciousness has to become practice, peace has to be given structure, and attention has to be spent the way a serious person spends money.",
      "Everything published under this territory is written to be used. If a teaching cannot survive contact with an ordinary Tuesday, it is not finished.",
    ],
    hubLinks: [
      { to: "/activations", label: "Daily activations", note: "The practice layer" },
      { to: "/academy", label: "The Academy", note: "Structured study" },
      { to: "/artist/buddha-ka", label: "Buddha KA", note: "The artist behind the framework" },
      { to: "/membership", label: "Membership", note: "Ongoing access to the system" },
    ],
  },
  {
    slug: "music-and-meaning",
    name: "Music & Meaning",
    definition:
      "Music and meaning is the study of what the songs are actually saying — release worlds, lyrical interpretation, and the spiritual context a record is built inside.",
    description:
      "Albums and songs read closely: what a record is about, why it is sequenced the way it is, and how its language connects to the wider teaching.",
    aliases: ["music and meaning", "song meaning", "lyrics", "lyric", "release world", "album", "behind the music", "hip hop", "hip-hop"],
    keywords: ["song meaning", "lyrical", "the album", "tracklist"],
    territory: true,
    territoryOrder: 3,
    intro: [
      "A song is a container. This territory opens the containers — what a record means, what it is arguing, and what it asks of the person listening.",
      "Close reading here stays honest to the work: interpretation is offered as interpretation, and the recordings remain the primary source.",
    ],
    hubLinks: [
      { to: "/music", label: "All releases", note: "The Buddha KA catalog" },
      { to: "/music/discography", label: "Discography", note: "Every album and single in order" },
      { to: "/artist/buddha-ka", label: "Buddha KA", note: "Artist identity and streaming" },
      { to: "/beats", label: "Instrumentals", note: "The production catalog" },
    ],
  },
  {
    slug: "creative-architecture",
    name: "Creative Architecture",
    definition:
      "Creative architecture is Buddha KA's stated methodology for making work: The Eternal Buddha, Mirror Thinking, Mirror Writing, KA Flow, KA Resonance, and The Doorway.",
    description:
      "The method behind the catalog — how a line is tested, how cadence is built, and what a finished piece of work is supposed to do to the person receiving it.",
    aliases: ["creative architecture", "eternal buddha", "mirror thinking", "mirror writing", "ka flow", "ka resonance", "the doorway", "songwriting", "creative discipline", "method"],
    keywords: ["mirror thinking", "mirror writing", "ka flow", "ka resonance", "eternal buddha", "the doorway"],
    territory: true,
    territoryOrder: 5,
    intro: [
      "Every artist has a method, whether or not they can name it. This one is named on purpose, so it can be taught, argued with, and held to a standard.",
      "Six components, six tests. Timeless foundation. A lens for hidden structure. Lines that earn their place. Cadence that moves like water. Emotion that carries current. And a doorway the listener actually walks through.",
    ],
    hubLinks: [
      { to: "/music", label: "The catalog", note: "The method's output" },
      { to: "/beats", label: "Instrumentals", note: "Where cadence is built" },
      { to: "/artist/buddha-ka", label: "Buddha KA", note: "The artist and the method" },
      { to: "/books", label: "Books", note: "The method written down" },
    ],
  },
  {
    slug: "consciousness-architecture",
    name: "Consciousness Architecture",
    definition:
      "Consciousness architecture is the design of an interior life — attention, perception, reaction, boundaries, and the infrastructure that makes self-mastery repeatable.",
    description:
      "Attention discipline, energetic ownership, sacred accounting, nervous-system freedom, and field governance treated as buildable structure rather than mood.",
    aliases: ["consciousness architecture", "attention", "perception", "reaction", "nervous system freedom", "self-mastery", "inner infrastructure", "sacred accounting", "field governance", "energetic ownership", "boundaries", "internal design"],
    keywords: ["attention discipline", "inner infrastructure", "sacred accounting", "field governance", "energetic ownership", "self-mastery"],
    territory: true,
    territoryOrder: 6,
    intro: [
      "Freedom is not a feeling that arrives. It is infrastructure: what you let in, what you answer, what you refuse, and what you have built to hold you when the day goes sideways.",
      "This territory is the practical wing of the work — attention treated as an account, reaction treated as a trained reflex, and boundaries treated as architecture rather than apology.",
    ],
    hubLinks: [
      { to: "/activations", label: "Daily activations", note: "Attention trained on a schedule" },
      { to: "/gates", label: "The 47 Gates", note: "The map of consciousness" },
      { to: "/artist/buddha-ka", label: "Buddha KA", note: "The artist behind the teaching" },
      { to: "/academy", label: "The Academy", note: "Structured lessons" },
    ],
  },
] as const;

/** The six permanent authority territories, in display order. */
export const TERRITORIES: readonly Topic[] = TOPICS.filter((t) => t.territory).sort(
  (a, b) => (a.territoryOrder ?? 99) - (b.territoryOrder ?? 99),
);


const BY_SLUG = new Map(TOPICS.map((t) => [t.slug, t]));

export function getTopic(slug: string): Topic | undefined {
  return BY_SLUG.get(slug);
}

export function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

/**
 * Score how strongly an item belongs to a topic.
 * Tag matches are authoritative (weight 3); text matches are supporting (1).
 * A score of 0 means no repository-backed relationship — do not link.
 */
export function topicScore(
  topic: Topic,
  input: { tags?: (string | null | undefined)[] | null; text?: string | null },
): number {
  let score = 0;
  const aliases = new Set(topic.aliases);
  for (const raw of input.tags ?? []) {
    if (!raw) continue;
    const t = normalizeTag(raw);
    if (aliases.has(t)) score += 3;
    else if ([...aliases].some((a) => a.length > 4 && t.includes(a))) score += 2;
  }
  const text = (input.text ?? "").toLowerCase();
  if (text) {
    for (const k of topic.keywords) if (text.includes(k)) score += 1;
  }
  return score;
}

/** Topics an item belongs to, strongest first. */
export function topicsFor(
  input: { tags?: (string | null | undefined)[] | null; text?: string | null },
  limit = 3,
): Topic[] {
  return TOPICS.map((t) => ({ t, s: topicScore(t, input) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.t);
}

export const TAXONOMY_VERSION = "1.0";
